import { get, patch, post } from 'aws-amplify/api'
import { GetOperation, PostOperation } from '@aws-amplify/api-rest/src/types'
import { TemplateDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'

enum Api {
    Name = 'tuhiDev',

    // usage: /patient/{predicate}
    FetchPatient = '/patient/',

    // usage: /subscriptionPricing
    FetchSubscriptionPricing = '/subscriptionPricing/',

    // usage: /upgradeSubscription/{userId}
    CreateSubscription = '/createSubscription/',

    // usage: /upgradeSubscription/{sessionId}
    PaymentSuccess = '/validateSubscription/',

    // usage: /cancleSubscription/{userId}
    CancelSubscription = '/cancelSubscription/',

    // usage: /update-consult-patient/{consultId}
    MatchPatientToConsult = '/update-consult-patient/',

    // usage: /processQrAuthenticate/{browserToken}
    ProcessQrAuthentication = '/processQrAuthentication/',

    // usage: /completeQrAuthenticate/{browserToken}
    CompleteQrAuthentication = '/completeQrAuthentication/',

    // usage: /userInfo/{accessToken}
    UserInfo = '/userInfo/',

    // usage: /processAttachment/{consultId}
    ProcessAttachment = '/processAttachment/',

    // usage: /processAssistant/{assistantId}
    ProcessAssistant = '/processAssistant/',

    // usage: /processForm/{assistantId}
    ProcessForm = '/processForm/',

    // usage: /processTemplateDelta/{consultId}
    ProcessTemplateDelta = '/processTemplateDelta/',

    // usage: //processSubscriptionCheckForExemption/{userId}
    ProcessEligibilityCheck = '/processSubscriptionCheckForExemption/',

    // usage: //processSubscriptionAccessCode/{userId}
    ProcessSubscriptionAccessCode = '/processSubscriptionAccessCode/',

    // usage: //retryConsult/{consultId}
    RetryConsult = '/retryConsult/',

    // usage: /processDefaultAccuracyTemplate/{consultId}
    ProcessDefaultAccuracyTemplate = '/processDefaultAccuracyTemplate/',

    // usage: /processDefaultAccuracyTemplate/{consultId}
    SubmitFormToFHIR = '/submitFormToFHIR/',

    // usage: /downloadFormPDF/{formId}
    DownloadFormPDF = '/downloadFormPDF/',

    // usage: /expandValueSet
    ExpandValueSet = '/expandValueSet/',

    // usage: /markFormEnteredInError/{formId}
    MarkFormEnteredInError = '/markFormEnteredInError/',

    // usage: /sendUserNotification/{accessToken}  (admin-only, POST body: adminUserId, subject, body, recipientUserIds)
    SendUserNotification = '/sendUserNotification/',

    // usage: /orgConsultCounts/  (admin-only, POST body: accessToken, subscriptionId, since)
    // ACA-140: per-user consult counts computed server-side over IAM, because
    // the OIDC owner rule on ConsultDto only ever returns the caller's own.
    // Trailing slash required: the authRole execute-api grant is
    // `.../orgConsultCounts/*`, which does not match the bare path (same as
    // /expandValueSet/).
    OrgConsultCounts = '/orgConsultCounts/',

    // usage: /accDocuments/  (POST body: accessToken, formId, claimReferenceNumber, contentType)
    // ACA-16: render a completed form's PDF and send it to ACC's Documents
    // API (the fake ACC on DEV until ACC issues a key). Trailing slash for the
    // same authRole execute-api grant reason as /orgConsultCounts/.
    SubmitAccDocument = '/accDocuments/',

    // ACA-37 Encounter Evidence (SigV4; the Lambdas check consult ownership).
    // usage: /consultEvidence/{consultId}  (POST body: fileName, mimeType, sizeBytes)
    CreateConsultEvidence = '/consultEvidence/',

    // usage: /consultEvidenceFromAttachment/{consultId}  (POST body: attachmentId)
    CreateEvidenceFromAttachment = '/consultEvidenceFromAttachment/',

    // usage: /consultEvidenceInclude/{consultId}/{evidenceId}  (PATCH body: included)
    SetEvidenceIncluded = '/consultEvidenceInclude/',

    // usage: /consultEvidenceRetry/{consultId}/{evidenceId}  (POST, no body)
    RetryConsultEvidence = '/consultEvidenceRetry/',

    // usage: /regenerateConsult/{consultId}  (POST, no body; ACA-37 Req 4)
    RegenerateConsult = '/regenerateConsult/',

    // usage: /restoreConsultVersion/{consultId}/{version}  (POST, no body; ACA-37 Req 5.7)
    RestoreConsultVersion = '/restoreConsultVersion/',

    // ACA-176 delegation (SigV4; the Lambda checks grants and organisation).
    // usage: /consultDelegation/{action}  (POST; action = candidates | list |
    // grant | revoke | shared | view)
    ConsultDelegation = '/consultDelegation/',
}

enum RequestParameters {
    UserId = 'userId',
    FullName = 'fullName',
    DateOfBirth = 'dateOfBirth',
    Gender = 'gender',
    PatientId = 'patientId',
    Name = 'name',
    Email = 'email',
    MimeType = 'mimeType',
    AttachmentType = 'attachmentType',
    Attachment = 'attachment',
    ConsultId = 'consultId',
    Template = 'template',
    AccessCode = 'accessCode',
    AssistantId = 'assistantId',
    PriceId = 'priceId',
    CancellationReason = 'reason',
    IsDefaultTemplate = 'isDefaultTemplate',
    formId = 'formId',
    TemplateId = 'templateId',
    // ACA-189: identify a catalogue template (+ version) added as an additional
    // template, so the backend generates from its prompt/config, not an assistant.
    CatalogueTemplateId = 'catalogueTemplateId',
    CatalogueTemplateVersion = 'catalogueTemplateVersion',
}

export class ApiHelper {
    static buildFetchPatientRestOperation(
        predicate: string,
        userId: string
    ): GetOperation {
        const prefix = Api.FetchPatient
        const path = prefix + predicate

        const queryParameters = {
            [RequestParameters.UserId]: userId,
        }

        return get({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    /**
     * Build the demographic ("match") patient search operation (ACA-11).
     *
     * When the path predicate is not a valid NHI, the backend routes to the
     * demographic match flow and reads fullName / dateOfBirth / gender from the
     * query string, returning a scored list of candidate patients (highest
     * first). The name is used as the (non-NHI) path predicate purely to select
     * that flow.
     */
    static buildMatchPatientRestOperation(
        search: { fullName: string; dateOfBirth: string; gender?: string },
        userId: string
    ): GetOperation {
        const path = Api.FetchPatient + encodeURIComponent(search.fullName)

        const queryParameters: Record<string, string> = {
            [RequestParameters.UserId]: userId,
            [RequestParameters.FullName]: search.fullName,
            [RequestParameters.DateOfBirth]: search.dateOfBirth,
        }
        if (search.gender) {
            queryParameters[RequestParameters.Gender] = search.gender
        }

        return get({
            apiName: Api.Name,
            path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildFetchSubscriptionPricingRestOperation(): GetOperation {
        const path = Api.FetchSubscriptionPricing

        return get({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildCreateCheckoutSessionRestOperation(
        userId: string,
        priceId: string
    ): GetOperation {
        const prefix = Api.CreateSubscription

        const path = prefix + userId

        const queryParameters = {
            [RequestParameters.PriceId]: priceId,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildPaymentSuccessRestOperation(sessionId: string): GetOperation {
        const prefix = Api.PaymentSuccess

        const path = prefix + sessionId

        return post({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildCancelSubscriptionRestOperation(
        userId: string,
        reason: string
    ): GetOperation {
        const prefix = Api.CancelSubscription

        const path = prefix + userId

        const queryParameters = {
            [RequestParameters.CancellationReason]: reason,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildUpdateConsultWithPatientRestOperation(
        consultId: string,
        nhi: string
    ): GetOperation {
        const prefix = Api.MatchPatientToConsult
        const path = prefix + consultId

        const queryParameters = {
            [RequestParameters.PatientId]: nhi,
        }

        return get({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildProcessQrAuthenticationOperation(
        browserToken: string
    ): GetOperation {
        const prefix = Api.ProcessQrAuthentication
        const path = prefix + browserToken

        logger.debug('Building QR authentication operation', {
            action: 'build_qr_auth_operation',
            metadata: { path },
        })

        return get({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildCompleteQrAuthenticationOperation(
        browserToken: string
    ): GetOperation {
        const prefix = Api.CompleteQrAuthentication
        const path = prefix + browserToken

        logger.debug('Building complete QR authentication operation', {
            action: 'build_complete_qr_auth_operation',
            metadata: { path },
        })

        return get({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildFetchUserInfoOperation(accessToken: string): GetOperation {
        const prefix = Api.UserInfo
        const path = prefix + accessToken

        return get({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildProcessAttachmentOperation(
        consultId: string,
        userId: string,
        mimeType: string,
        attachmentType: string,
        attachment: string
    ): GetOperation {
        const prefix = Api.ProcessAttachment
        const path = prefix + consultId

        logger.debug('Building process attachment operation', {
            action: 'build_process_attachment_operation',
            metadata: { path, consultId, userId, mimeType, attachmentType },
        })

        const queryParameters = {
            [RequestParameters.UserId]: userId,
            [RequestParameters.MimeType]: mimeType,
            [RequestParameters.AttachmentType]: attachmentType,
            [RequestParameters.Attachment]: attachment,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildProcessAssistantOperation(
        assistantId: string,
        consultId: string,
        userId: string,
        catalogue?: {
            catalogueTemplateId: string
            catalogueTemplateVersion: number
        }
    ): GetOperation {
        const prefix = Api.ProcessAssistant
        // ACA-189: the route is /processAssistant/{assistantId}, so it needs a
        // non-empty path segment. A catalogue template has no assistantId, so we
        // put its recordId in the segment purely to satisfy routing — the backend
        // ignores the segment when the catalogueTemplateId query param is present.
        const pathSegment = assistantId || catalogue?.catalogueTemplateId || ''
        const path = prefix + pathSegment

        logger.debug('Building process assistant operation', {
            action: 'build_process_assistant_operation',
            metadata: { path, assistantId, consultId, userId, catalogue },
        })

        const queryParameters: Record<string, string> = {
            [RequestParameters.ConsultId]: consultId,
            [RequestParameters.UserId]: userId,
        }
        if (catalogue) {
            queryParameters[RequestParameters.CatalogueTemplateId] =
                catalogue.catalogueTemplateId
            queryParameters[RequestParameters.CatalogueTemplateVersion] =
                String(catalogue.catalogueTemplateVersion)
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildProcessTemplateDelta(
        consultId: string,
        template: TemplateDto,
        isDefaultTemplate: boolean
    ): GetOperation {
        const prefix = Api.ProcessTemplateDelta
        const path = prefix + consultId

        logger.debug('Building process template delta operation', {
            action: 'build_process_template_delta_operation',
            metadata: { path, consultId, isDefaultTemplate },
        })

        // Send template in request body instead of query parameters
        // This allows for much larger payloads (up to 10MB vs ~8KB for query strings)
        return post({
            apiName: Api.Name,
            path: path,
            options: {
                body: {
                    template: template,
                    isDefaultTemplate: isDefaultTemplate,
                },
            },
        })
    }

    static buildProcessSubscriptionEligibility(
        userId: string,
        email: string
    ): GetOperation {
        const prefix = Api.ProcessEligibilityCheck
        const path = prefix + userId

        logger.debug('Building process subscription eligibility operation', {
            action: 'build_process_subscription_eligibility_operation',
            metadata: { path, userId, email },
        })

        const queryParameters = {
            [RequestParameters.Email]: JSON.stringify(email),
        }

        return get({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildProcessSubscriptionAccessCode(
        userId: string,
        accessCode: string
    ): GetOperation {
        const prefix = Api.ProcessSubscriptionAccessCode
        const path = prefix + userId

        logger.debug('Building process subscription access code operation', {
            action: 'build_process_subscription_access_code_operation',
            metadata: { path, userId },
        })

        const queryParameters = {
            [RequestParameters.AccessCode]: JSON.stringify(accessCode),
        }

        return get({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildRetryConsult(consultId: string): GetOperation {
        const prefix = Api.RetryConsult
        const path = prefix + consultId

        logger.debug('Building retry consult operation', {
            action: 'build_retry_consult_operation',
            metadata: { path, consultId },
        })

        return get({
            apiName: Api.Name,
            path: path,
        })
    }

    static buildProcessDefaultAccuracyTemplateOperation(
        consultId: string
    ): GetOperation {
        const prefix = Api.ProcessDefaultAccuracyTemplate
        const path = prefix + consultId

        return post({
            apiName: Api.Name,
            path: path,
            options: {},
        })
    }

    static buildProcessFormOperation(
        assistantId: string,
        consultId: string,
        userId: string,
        templateId?: string
    ): GetOperation {
        const prefix = Api.ProcessForm
        const path = prefix + assistantId

        logger.debug('Building process form operation', {
            action: 'build_process_form_operation',
            metadata: { path, assistantId, consultId, userId, templateId },
        })

        const queryParameters = {
            [RequestParameters.ConsultId]: consultId,
            [RequestParameters.UserId]: userId,
            ...(templateId !== undefined && {
                [RequestParameters.TemplateId]: templateId,
            }),
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildSubmitFormToFHIROperation(
        formId: string,
        userId: string
    ): GetOperation {
        const prefix = Api.SubmitFormToFHIR
        const path = prefix + formId

        logger.debug('Building submit form form to FHIR operation', {
            action: 'build_submit_form_operation',
            metadata: { path, formId, userId },
        })

        const queryParameters = {
            [RequestParameters.UserId]: userId,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildDownloadFormPDFOperation(
        formId: string,
        userId: string
    ): GetOperation {
        const prefix = Api.DownloadFormPDF
        const path = prefix + formId

        logger.debug('Building download form PDF operation', {
            action: 'build_download_pdf_operation',
            metadata: { path, formId, userId },
        })

        const queryParameters = {
            [RequestParameters.UserId]: userId,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    static buildExpandValueSetOperation(
        valueSetUrl: string,
        userId: string
    ): PostOperation {
        const prefix = Api.ExpandValueSet
        const path = prefix

        logger.debug('Building expand ValueSet operation', {
            action: 'build_expand_valueset_operation',
            metadata: { path, valueSetUrl, userId },
        })

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                body: {
                    valueSetUrl,
                    userId,
                },
            },
        })
    }

    static buildMarkFormEnteredInErrorOperation(
        formId: string,
        userId: string
    ): GetOperation {
        const prefix = Api.MarkFormEnteredInError
        const path = prefix + formId

        logger.debug('Building mark form entered in error operation', {
            action: 'build_mark_form_error_operation',
            metadata: {
                prefix,
                formId,
                formIdType: typeof formId,
                formIdValue: JSON.stringify(formId),
                formIdLength: formId?.length,
                path,
                userId,
                fullUrl: `${prefix}${formId}?userId=${userId}`,
            },
        })

        const queryParameters = {
            [RequestParameters.UserId]: userId,
        }

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                queryParams: queryParameters,
            },
        })
    }

    /**
     * ACA-146: admin-only transactional user-notification send.
     * The OIDC access token rides in the path (as with UserInfo); the admin
     * identity, subject/body and recipient ids go in the POST body. The backend
     * binds the token `sub` to `adminUserId`, requires role=admin, and validates
     * every recipient against the admin's subscription.
     */
    static buildSendUserNotificationOperation(
        accessToken: string,
        payload: {
            adminUserId: string
            subject: string
            body: string
            recipientUserIds: string[]
        }
    ): GetOperation {
        const path = Api.SendUserNotification + accessToken

        logger.debug('Building send user notification operation', {
            action: 'build_send_user_notification_operation',
            metadata: {
                adminUserId: payload.adminUserId,
                recipientCount: payload.recipientUserIds.length,
            },
        })

        return post({
            apiName: Api.Name,
            path: path,
            options: {
                body: payload,
            },
        })
    }

    /**
     * ACA-140: admin-only org consult counts. The access token travels in the
     * body (not the path) so it stays out of API Gateway access logs.
     */
    static buildOrgConsultCountsOperation(payload: {
        accessToken: string
        subscriptionId: string
        since: string
    }): GetOperation {
        logger.debug('Building org consult counts operation', {
            action: 'build_org_consult_counts_operation',
            metadata: { subscriptionId: payload.subscriptionId },
        })

        return post({
            apiName: Api.Name,
            path: Api.OrgConsultCounts,
            options: {
                body: payload,
            },
        })
    }

    /**
     * ACA-16: send a completed form to ACC. The access token travels in the
     * body (not the path) so it stays out of API Gateway access logs; the
     * backend reads the form and consult as the caller, so only the owner of
     * the consult can send it.
     */
    static buildSubmitAccDocumentOperation(payload: {
        accessToken: string
        formId: string
        claimReferenceNumber: string
        contentType: string
    }): GetOperation {
        logger.debug('Building submit ACC document operation', {
            action: 'build_submit_acc_document_operation',
            metadata: { formId: payload.formId },
        })

        return post({
            apiName: Api.Name,
            path: Api.SubmitAccDocument,
            options: {
                body: payload,
            },
        })
    }

    /**
     * ACA-37: create an uploaded evidence item and get its presigned PUT.
     * The file name is not logged: it can carry patient detail.
     */
    static buildCreateConsultEvidenceOperation(
        consultId: string,
        payload: { fileName: string; mimeType: string; sizeBytes: number }
    ): GetOperation {
        return post({
            apiName: Api.Name,
            path: Api.CreateConsultEvidence + encodeURIComponent(consultId),
            options: {
                body: payload,
            },
        })
    }

    /** ACA-37: include an existing consult attachment as evidence (Req 1.7). */
    static buildCreateEvidenceFromAttachmentOperation(
        consultId: string,
        attachmentId: string
    ): GetOperation {
        return post({
            apiName: Api.Name,
            path:
                Api.CreateEvidenceFromAttachment +
                encodeURIComponent(consultId),
            options: {
                body: { attachmentId },
            },
        })
    }

    /** ACA-37: include or exclude an evidence item (Req 3.4). */
    static buildSetEvidenceIncludedOperation(
        consultId: string,
        evidenceId: string,
        included: boolean
    ): GetOperation {
        return patch({
            apiName: Api.Name,
            path:
                Api.SetEvidenceIncluded +
                encodeURIComponent(consultId) +
                '/' +
                encodeURIComponent(evidenceId),
            options: {
                body: { included },
            },
        }) as unknown as GetOperation
    }

    static buildRetryConsultEvidenceOperation(
        consultId: string,
        evidenceId: string
    ): GetOperation {
        return post({
            apiName: Api.Name,
            path:
                Api.RetryConsultEvidence +
                encodeURIComponent(consultId) +
                '/' +
                encodeURIComponent(evidenceId),
        })
    }

    static buildRegenerateConsultOperation(consultId: string): GetOperation {
        return post({
            apiName: Api.Name,
            path: Api.RegenerateConsult + encodeURIComponent(consultId),
        })
    }

    /** ACA-176: one delegation action (see Api.ConsultDelegation). */
    static buildConsultDelegationOperation(
        action: string,
        body: Record<string, unknown> = {}
    ): GetOperation {
        return post({
            apiName: Api.Name,
            path: Api.ConsultDelegation + encodeURIComponent(action),
            options: { body: body as Record<string, string> },
        })
    }

    static buildRestoreConsultVersionOperation(
        consultId: string,
        version: number
    ): GetOperation {
        return post({
            apiName: Api.Name,
            path:
                Api.RestoreConsultVersion +
                encodeURIComponent(consultId) +
                '/' +
                String(version),
        })
    }
}
